import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';
import {
    AddNewIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    CloseIcon,
    DeleteIcon,
    EditIcon,
    HerdIcon,
    HelpIcon,
    LogOutIcon,
    NoteIcon,
    OverviewIcon,
    ProfileIcon,
    SearchIcon,
    SettingsIcon,
    TaskIcon,
    SignalIcon,
    TankIcon,
} from 'app/agriculture/icons';
import './Button.scss';
import { DefaultViewIcon } from 'app/agriculture/icons/DefaultViewIcon';

export type ButtonVariant = 'contained' | 'outlined' | 'textonly' | 'compact' | 'inline';
export type ButtonTheme = 'primary' | 'dark' | 'danger' | 'warning' | 'success' | 'skeleton';

const supportedIcons: Record<string, ReactNode> = {
    add: <AddNewIcon />,
    back: <ArrowLeftIcon />,
    close: <CloseIcon />,
    delete: <DeleteIcon />,
    device: <SignalIcon />,
    edit: <EditIcon />,
    goto: <ArrowRightIcon />,
    help: <HelpIcon />,
    herd: <HerdIcon />,
    logout: <LogOutIcon />,
    note: <NoteIcon />,
    overview: <OverviewIcon />,
    profile: <ProfileIcon />,
    search: <SearchIcon />,
    settings: <SettingsIcon />,
    defaultView: <DefaultViewIcon />,
    task: <TaskIcon />,
    tank: <TankIcon />,
};

type IconType = ReactNode | keyof typeof supportedIcons;

interface ButtonBaseProps {
    onClick?: () => void;
    id?: string;
    className?: string;
    variant?: ButtonVariant;
    theme?: ButtonTheme;
    isLoading?: boolean;
    disabled?: boolean;
    children: ReactNode;
    tooltip?: string;
    fullWidth?: boolean;
}

const ButtonBase = forwardRef<
    HTMLButtonElement,
    HTMLAttributes<HTMLButtonElement> & ButtonBaseProps
>(function ButtonBase(
    {
        id,
        className,
        onClick,
        children,
        variant = 'contained',
        theme = 'primary',
        isLoading = false,
        disabled = false,
        tooltip,
        fullWidth = false,
        ...htmlProps
    },
    ref,
) {
    return (
        <button
            ref={ref}
            type="button"
            {...htmlProps}
            className={classNames('Button', className, `Button--${variant}`, `Button--${theme}`, {
                'Button--loading': isLoading,
                'Button--disabled': disabled,
                'Button--fullwidth': fullWidth,
            })}
            disabled={disabled || isLoading}
            data-testid={id ?? 'Button'}
            onClick={onClick}
            title={tooltip}
        >
            <span className="Button__content">{children}</span>
        </button>
    );
});

interface ButtonProps extends Omit<ButtonBaseProps, 'children'> {
    text: string | number;
    startIcon?: IconType;
    endIcon?: IconType;
    type?: string;
}

export const Button = forwardRef<
    HTMLButtonElement,
    HTMLAttributes<HTMLButtonElement> & ButtonProps
>(function Button({ startIcon, text, endIcon, ...buttonBaseProps }, ref) {
    return (
        <ButtonBase {...buttonBaseProps} ref={ref} tooltip={String(text)}>
            {startIcon == null ? null : (
                <span className="Button__icon">{parseIcon(startIcon)}</span>
            )}
            <span className="Button__text">{text}</span>
            {endIcon == null ? null : <span className="Button__icon">{parseIcon(endIcon)}</span>}
        </ButtonBase>
    );
});

interface InlineButtonProps extends Omit<ButtonBaseProps, 'children' | 'variant'> {
    text: string | number;
}

export const InlineButton = forwardRef<
    HTMLButtonElement,
    HTMLAttributes<HTMLButtonElement> & InlineButtonProps
>(function Button({ text, ...buttonBaseProps }, ref) {
    return (
        <ButtonBase {...buttonBaseProps} variant="inline" ref={ref} tooltip={String(text)}>
            {text}
        </ButtonBase>
    );
});

interface IconButtonProps extends Omit<ButtonBaseProps, 'children'> {
    icon: IconType;
    tooltip: string;
    size?: number | string;
    shouldRevealTextOnHover?: boolean;
}

export const IconButton = forwardRef<
    HTMLButtonElement,
    IconButtonProps & Omit<HTMLAttributes<HTMLButtonElement>, 'icon'>
>(function IconButton({ icon, shouldRevealTextOnHover = false, ...buttonBaseProps }, ref) {
    return (
        <ButtonBase
            {...buttonBaseProps}
            ref={ref}
            className={classNames('Button--icon', buttonBaseProps.className, {
                'Button--icon--shouldRevealTextOnHover': shouldRevealTextOnHover,
            })}
        >
            <span className="Button__icon">{parseIcon(icon)}</span>
            <span className="Button__text">{buttonBaseProps.tooltip}</span>
        </ButtonBase>
    );
});

function parseIcon(icon: IconType): ReactNode {
    if (typeof icon === 'string') {
        return supportedIcons[icon];
    }

    return icon;
}
